import { SagaIterator } from 'redux-saga';
import { select } from 'redux-saga/effects';
import {
  App,
  Produkt,
  Vertragsoptionen,
  WEITERE_NATURGEFAHREN_ID,
  ZuersZoneEnum,
} from '@ps20-frontend/frontend-models';
import {
  AngebotsId,
  GlobalAction,
  WrappedSavedStates,
  WrappedSavedStatesLoaded
} from '@ps20-frontend/shop-integration-redux';
import { ExtremwetterUWProcessQuery, isExtremwetterUWProcessNecessary } from '@ps20-frontend/selector';
import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import { Feature, HausArt, Stockwerk } from '@ps20/bff-models';
import { IAppState } from '../appState';

interface VhvV2StateForExtremwetterCleanup {
  angebotsId?: string;
  produkt?: Produkt;
  versObjekt?: {
    hausart?: HausArt;
    stockwerk?: Stockwerk;
  };
  elementarSelbstbehalt?: {
    id?: ZuersZoneEnum;
  };
  vorbelegungen?: {
    activateElementar?: boolean;
  };
  gisDaten?: {
    flutzone?: number;
  };
  vorschaeden?: {
    vorhanden?: boolean;
    elementar?: number;
  };
  risikoort?: {
    zuerszone?: number;
  };
  vertragsoptionen?: Vertragsoptionen;
}

interface WarenkorbAngebotForVhvExtremwetterCleanup {
  appId: App;
  angebotsId: string;
  beibehalten: boolean;
  extremwetterRisk?: boolean;
}

function hasWeitereNaturgefahrenSelected(produkt: Produkt | undefined): boolean {
  return getSelectedProduktlinie(produkt)?.zusatzoptionen?.some(
    zusatzoption => zusatzoption.id === WEITERE_NATURGEFAHREN_ID && zusatzoption.isSelected,
  ) === true;
}

function isMehrfamilienhausObergeschoss(versObjekt: VhvV2StateForExtremwetterCleanup['versObjekt']): boolean {
  return versObjekt?.hausart === HausArt.MEHRFAMILIENHAUS && versObjekt?.stockwerk === Stockwerk.OBERGESCHOSS;
}

function mapRawVhvStateToExtremwetterUWProcessQuery(
  vhvState: VhvV2StateForExtremwetterCleanup,
): ExtremwetterUWProcessQuery {
  return {
    elementarSelbstbehaltId: vhvState.elementarSelbstbehalt?.id,
    activateElementar: vhvState.vorbelegungen?.activateElementar,
    flutzone: vhvState.gisDaten?.flutzone,
    // Intentionally not the toggle-gated live value: the VHV_UW gate is already checked
    // before this recompute runs, and isExtremwetterUWProcessNecessary never reads this field.
    featureToggles: undefined,
    vorschaedenVorhanden: vhvState.vorschaeden?.vorhanden,
    vertragsoptionen: vhvState.vertragsoptionen ?? ({} as Vertragsoptionen),
    // Not persisted in the saved state; the live VHV_V2 selector defaults it to 0 too.
    elementarVorschadenCountFromRisikoortService: 0,
    risikoortZuerszone: vhvState.risikoort?.zuerszone,
    elementarVorschaedenCount: vhvState.vorschaeden?.elementar,
  };
}

function isObsoleteVhvExtremwetterProduct(
  angebot: WarenkorbAngebotForVhvExtremwetterCleanup,
  vhvStateByAngebotsId: Map<string, VhvV2StateForExtremwetterCleanup>,
): boolean {
  if (angebot.extremwetterRisk === true) {
    return false;
  }

  const vhvState = vhvStateByAngebotsId.get(angebot.angebotsId);
  if (!vhvState) {
    return false;
  }

  if (!hasWeitereNaturgefahrenSelected(vhvState.produkt)) {
    return false;
  }

  if (isMehrfamilienhausObergeschoss(vhvState.versObjekt)) {
    return false;
  }

  return isExtremwetterUWProcessNecessary(mapRawVhvStateToExtremwetterUWProcessQuery(vhvState));
}

export function* collectOldVhvExtremwetterProducts(
  action: GlobalAction<WrappedSavedStatesLoaded>,
): SagaIterator<AngebotsId[]> {
  const isVhvUwToggleEnabled: boolean = (yield select(
    (s: IAppState) => s.transientState?.featureToggles?.[Feature.VHV_UW] === true,
  )) as boolean;

  if (!isVhvUwToggleEnabled) {
    return [];
  }

  const actionPayload: WrappedSavedStates = action.payload.savedStatesLoaded;
  const loadedAppState = actionPayload.find(
    loadedState => loadedState.appId === App.SHOP_DASHBOARD
  );

  if (!loadedAppState) {
    return [];
  }

  const warenkorb = loadedAppState.state.warenkorb as WarenkorbAngebotForVhvExtremwetterCleanup[];

  if (!warenkorb || warenkorb.length === 0) {
    return [];
  }

  const vhvV2Candidates = warenkorb.filter(
    angebot => angebot.appId === App.VHV_V2 && angebot.beibehalten !== true,
  );

  if (vhvV2Candidates.length === 0) {
    return [];
  }

  const vhvV2StatesMap = new Map<string, VhvV2StateForExtremwetterCleanup>();
  actionPayload
    .filter(loadedState => loadedState.appId === App.VHV_V2)
    .forEach(vhvV2State => {
      const typedState = vhvV2State.state as VhvV2StateForExtremwetterCleanup;
      const angebotsId = typedState.angebotsId;
      if (angebotsId) {
        vhvV2StatesMap.set(angebotsId, typedState);
      }
    });

  return vhvV2Candidates.filter(
    angebot => isObsoleteVhvExtremwetterProduct(angebot, vhvV2StatesMap),
  );
}
